(() => {
  const ROOT_SELECTOR =
    '.fw-rush-desk[data-fw-rush-rail-page]';

  const TRACK_SELECTOR =
    '[data-fw-rush-rail-track]';

  const RAIL_SELECTOR =
    '[data-fw-rush-rail]';

  const FORM_SELECTOR =
    '.fw-rush-desk__form';

  const HEADER_SELECTOR =
    '.fw-header';

  const VIEWPORT_CLASS =
    'fw-rush-desk__rail-viewport';

  const desktopQuery =
    window.matchMedia(
      '(min-width: 991px)'
    );

  const HEADER_GAP =
    24;

  const VIEWPORT_BOTTOM_GAP =
    24;

  const clamp = (
    value,
    min,
    max
  ) => {
    return Math.min(
      Math.max(
        value,
        min
      ),
      max
    );
  };

  const getScrollY = () => {
    return (
      window.scrollY ||
      document.documentElement.scrollTop ||
      0
    );
  };

  const initRoot = (root) => {
    if (
      !root ||
      root.dataset.fwRushRailReady ===
        'true'
    ) {
      return;
    }

    const track =
      root.querySelector(
        TRACK_SELECTOR
      );

    const rail =
      root.querySelector(
        RAIL_SELECTOR
      );

    const form =
      root.querySelector(
        FORM_SELECTOR
      );

    const header =
      document.querySelector(
        HEADER_SELECTOR
      );

    if (
      !track ||
      !rail ||
      !form
    ) {
      return;
    }

    const viewport =
      document.createElement(
        'div'
      );

    viewport.className =
      VIEWPORT_CLASS;

    viewport.setAttribute(
      'data-fw-rush-rail-viewport',
      ''
    );

    rail.before(
      viewport
    );

    viewport.appendChild(
      rail
    );

    root.dataset.fwRushRailReady =
      'true';

    let frame =
      null;

    const isDesktop = () => {
      return desktopQuery.matches;
    };

    const clearGeometry = () => {
      root.style.removeProperty(
        '--fw-rush-rail-sticky-top'
      );

      root.style.removeProperty(
        '--fw-rush-rail-viewport-height'
      );

      root.style.removeProperty(
        '--fw-rush-rail-offset'
      );

      track.style.removeProperty(
        'height'
      );

      track.style.removeProperty(
        'min-height'
      );

      track.style.removeProperty(
        'align-self'
      );

      form.style.removeProperty(
        'align-self'
      );

      viewport.style.removeProperty(
        'overflow'
      );

      viewport.classList.remove(
        'is-virtual',
        'is-active'
      );
    };

    const render = () => {
      frame =
        null;

      if (!isDesktop()) {
        clearGeometry();

        return;
      }

      /*
       * The form must retain its natural height.
       *
       * Without this override, the grid's default stretch
       * behavior can make the form inherit a taller height
       * from the rail column before we measure it.
       */
      form.style.alignSelf =
        'start';

      track.style.alignSelf =
        'start';

      const scrollY =
        getScrollY();

      const headerBottom =
        header
          ? Math.max(
              0,
              header
                .getBoundingClientRect()
                .bottom
            )
          : 0;

      const stickyTop =
        headerBottom +
        HEADER_GAP;

      const availableHeight =
        Math.max(
          160,
          window.innerHeight -
            stickyTop -
            VIEWPORT_BOTTOM_GAP
        );

      const formHeight =
        form
          .getBoundingClientRect()
          .height;

      const railHeight =
        rail.scrollHeight;

      const viewportHeight =
        Math.min(
          railHeight,
          availableHeight
        );

      const maxOffset =
        Math.max(
          0,
          railHeight -
            viewportHeight
        );

      /*
       * The Rush Desk page height is driven by the form,
       * not by the combined height of the information cards.
       *
       * The track only receives enough height to match the
       * form, with a minimum equal to the visible rail
       * viewport when necessary.
       */
      const trackHeight =
        Math.max(
          formHeight,
          viewportHeight
        );

      root.style.setProperty(
        '--fw-rush-rail-sticky-top',
        `${stickyTop.toFixed(2)}px`
      );

      root.style.setProperty(
        '--fw-rush-rail-viewport-height',
        `${viewportHeight.toFixed(2)}px`
      );

      track.style.height =
        `${Math.ceil(trackHeight)}px`;

      track.style.minHeight =
        `${Math.ceil(trackHeight)}px`;

      /*
       * The rail itself can be taller than the visible
       * sticky viewport. Hide the unrevealed portion.
       */
      viewport.style.overflow =
        'hidden';

      /*
       * Keep the rail constrained to the Rush Desk section.
       * The old fixed-position mode must never be restored.
       */
      viewport.classList.remove(
        'is-virtual',
        'is-active'
      );

      const trackRect =
        track.getBoundingClientRect();

      const trackDocumentTop =
        scrollY +
        trackRect.top;

      const stickyStart =
        Math.max(
          0,
          trackDocumentTop -
            stickyTop
        );

      const stickyTravel =
        Math.max(
          0,
          trackHeight -
            viewportHeight
        );

      let progress =
        0;

      if (
        maxOffset > 0 &&
        stickyTravel > 0
      ) {
        progress =
          clamp(
            (
              scrollY -
              stickyStart
            ) /
              stickyTravel,
            0,
            1
          );
      }

      /*
       * Map the entire rail's required internal movement
       * onto the vertical distance supplied by the form.
       *
       * This allows every information panel to become
       * visible without allowing the rail itself to make
       * the page taller.
       */
      const offset =
        maxOffset *
        progress;

      root.style.setProperty(
        '--fw-rush-rail-offset',
        `${offset.toFixed(2)}px`
      );
    };

    const scheduleRender = () => {
      if (
        frame !==
        null
      ) {
        return;
      }

      frame =
        window.requestAnimationFrame(
          render
        );
    };

    const handleResize = () => {
      scheduleRender();
    };

    window.addEventListener(
      'scroll',
      scheduleRender,
      {
        passive: true
      }
    );

    window.addEventListener(
      'resize',
      handleResize
    );

    desktopQuery.addEventListener?.(
      'change',
      handleResize
    );

    if (
      'ResizeObserver' in
      window
    ) {
      const resizeObserver =
        new ResizeObserver(
          scheduleRender
        );

      resizeObserver.observe(
        rail
      );

      resizeObserver.observe(
        form
      );

      if (header) {
        resizeObserver.observe(
          header
        );
      }
    }

    if (
      'MutationObserver' in
      window
    ) {
      const mutationObserver =
        new MutationObserver(
          scheduleRender
        );

      mutationObserver.observe(
        rail,
        {
          childList: true,
          subtree: true,
          characterData: true
        }
      );
    }

    if (
      document.fonts &&
      document.fonts.ready
    ) {
      document.fonts.ready.then(
        scheduleRender
      );
    }

    window.addEventListener(
      'load',
      scheduleRender,
      {
        once: true
      }
    );

    scheduleRender();
  };

  const initWithin = (
    scope = document
  ) => {
    if (
      scope.matches?.(
        ROOT_SELECTOR
      )
    ) {
      initRoot(
        scope
      );
    }

    scope
      .querySelectorAll?.(
        ROOT_SELECTOR
      )
      .forEach(
        initRoot
      );
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initWithin();
      },
      {
        once: true
      }
    );
  } else {
    initWithin();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initWithin(
        event.target
      );
    }
  );
})();